'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'sportstack-sidebar-state';
const STORAGE_VERSION = 1;

/** Larghezza massima (px) di un telefono: sotto, il menu parte collassato. */
export const PHONE_MAX_WIDTH_PX = 767;

interface SidebarState {
  version: number;
  isCollapsed: boolean;
  expandedGroups: string[];
  lastUpdated: number;
}

const DEFAULT_EXPANDED_GROUPS = [
  'Gestione Competizioni',
  'Gestione Organizzazioni',
  'Gestione Finanziaria',
  'Amministrazione Sistema'
];

const DEFAULT_STATE: SidebarState = {
  version: STORAGE_VERSION,
  isCollapsed: false,
  expandedGroups: DEFAULT_EXPANDED_GROUPS,
  lastUpdated: Date.now(),
};

/**
 * Hook for persisting sidebar state (collapsed state and expanded groups)
 * Features:
 * - localStorage persistence across sessions
 * - Cross-tab synchronization via storage events
 * - Graceful fallback if localStorage is unavailable
 * - Version migration support for future changes
 */
export function usePersistedSidebarState() {
  const [isHydrated, setIsHydrated] = useState(false);
  // Su telefono il menu non e' una colonna: sta nascosto e si apre sopra il contenuto.
  const [isPhone, setIsPhone] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(DEFAULT_STATE.isCollapsed);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(DEFAULT_STATE.expandedGroups);

  // Load state from localStorage after mount (avoid hydration mismatch)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: SidebarState = JSON.parse(stored);

        // Version check for future migrations
        if (parsed.version === STORAGE_VERSION) {
          setIsCollapsed(parsed.isCollapsed);
          setExpandedGroups(parsed.expandedGroups);
        } else {
          // Handle version migration if needed in the future
          console.info('Sidebar state version mismatch, using defaults');
        }
      }
    } catch (err) {
      console.warn('Failed to load sidebar state from localStorage:', err);
    }
    // Su telefono il menu e' nascosto del tutto (un pulsante lo apre sopra il
    // contenuto): sotto la soglia parte chiuso, qualunque cosa dica lo stato
    // salvato, e segue il ridimensionamento (rotazione, finestra).
    let query: MediaQueryList | null = null;
    let onChange: (() => void) | null = null;
    try {
      query = window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH_PX}px)`);
      setIsPhone(query.matches);
      if (query.matches) {
        setIsCollapsed(true);
      }
      const watched = query;
      onChange = () => {
        setIsPhone(watched.matches);
        if (watched.matches) setIsCollapsed(true);
      };
      watched.addEventListener?.('change', onChange);
    } catch {
      // matchMedia assente (ambiente senza finestra): resta lo stato letto.
    }
    setIsHydrated(true);
    return () => {
      if (query && onChange) query.removeEventListener?.('change', onChange);
    };
  }, []);

  // Persist state to localStorage on change
  useEffect(() => {
    if (!isHydrated) return;
    // Lo stato del telefono (chiuso/aperto sopra il contenuto) non e' la
    // preferenza di chi usa il computer: non si salva.
    if (isPhone) return;

    try {
      const state: SidebarState = {
        version: STORAGE_VERSION,
        isCollapsed,
        expandedGroups,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      // Handle localStorage full or disabled (privacy mode)
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        console.warn('localStorage full, sidebar state not persisted');
      } else {
        console.warn('Failed to persist sidebar state:', err);
      }
    }
  }, [isCollapsed, expandedGroups, isHydrated, isPhone]);

  // Sync across tabs via storage event
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed: SidebarState = JSON.parse(e.newValue);
          if (parsed.version === STORAGE_VERSION) {
            setIsCollapsed(parsed.isCollapsed);
            setExpandedGroups(parsed.expandedGroups);
          }
        } catch (err) {
          console.warn('Failed to parse sidebar state from storage event:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Memoized callbacks to prevent unnecessary re-renders
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const toggleGroup = useCallback((groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName]
    );
  }, []);

  const isGroupExpanded = useCallback((groupName: string) => {
    return expandedGroups.includes(groupName);
  }, [expandedGroups]);

  const expandAll = useCallback(() => {
    setExpandedGroups(DEFAULT_EXPANDED_GROUPS);
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedGroups([]);
  }, []);

  // Memoize return value to prevent unnecessary re-renders of consumers
  return useMemo(() => ({
    // State
    isCollapsed,
    expandedGroups,
    isHydrated,
    isPhone,

    // Actions
    toggleCollapsed,
    setIsCollapsed,
    toggleGroup,
    isGroupExpanded,
    expandAll,
    collapseAll,
  }), [
    isCollapsed,
    expandedGroups,
    isHydrated,
    isPhone,
    toggleCollapsed,
    toggleGroup,
    isGroupExpanded,
    expandAll,
    collapseAll,
  ]);
}

export type PersistedSidebarState = ReturnType<typeof usePersistedSidebarState>;
